import { LitElement, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "@phosphor-icons/webcomponents/PhCaretUp";
import "@phosphor-icons/webcomponents/PhCaretDown";
import "@phosphor-icons/webcomponents/PhCaretUpDown";

import { styles } from "./wow-base-table.css";
import type { TableColumn, SortState, SortDirection, TileRender } from "./types";
import "../skeleton-rect/skeleton-rect";

@customElement("wow-base-table")
export class WowBaseTable<T = Record<string, unknown>> extends LitElement {
  static override styles = [styles];

  @property({ type: Array })
  columns: TableColumn<T>[] = [];

  @property({ type: Array })
  data: T[] = [];

  @property({ type: Object })
  sortState: SortState = { column: null, direction: "none" };

  @property({ type: Object })
  sortStates: Record<string, SortDirection> = {};

  @property({ type: Boolean })
  loading = false;

  @property({ type: Number, attribute: "loading-rows" })
  loadingRows = 5;

  @property({ type: String, attribute: "empty-message" })
  emptyMessage = "No data available";

  @property({ type: Boolean })
  hoverable = true;

  @property({ type: Boolean, attribute: "show-header" })
  showHeader = true;

  @property({ type: Boolean, attribute: "disable-row-click" })
  disableRowClick = false;

  @property({ type: Number })
  breakpoint = 1024;

  @property({ attribute: false })
  tileRender?: TileRender<T>;

  @state() protected _isMobileView = false;
  @state() private _sortMenuOpen = false;

  private _mediaQuery?: MediaQueryList;
  private _mediaHandler = (e: MediaQueryListEvent | MediaQueryList) => {
    this._isMobileView = e.matches;
    if (!e.matches) this._sortMenuOpen = false;
  };
  private _sortMenuClickOutsideHandler?: (e: MouseEvent) => void;

  override connectedCallback() {
    super.connectedCallback();
    this.setupMediaQuery();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanupMediaQuery();
    this.cleanupSortMenuClickOutside();
  }

  override willUpdate(changed: Map<string, unknown>) {
    super.willUpdate(changed);
    if (changed.has("breakpoint")) {
      this.cleanupMediaQuery();
      this.setupMediaQuery();
    }
  }

  private colClass(key: string) {
    return `col-${key.replace(/_/g, "-")}`;
  }

  private setupMediaQuery() {
    this._mediaQuery = window.matchMedia(`(max-width: ${this.breakpoint - 1}px)`);
    this._mediaHandler(this._mediaQuery);
    this._mediaQuery.addEventListener("change", this._mediaHandler);
  }

  private cleanupMediaQuery() {
    this._mediaQuery?.removeEventListener("change", this._mediaHandler);
    this._mediaQuery = undefined;
  }

  override render() {
    if (this._isMobileView && this.tileRender) {
      return this.renderMobileView();
    }

    const hasData = this.data.length > 0;
    const tableClass = this.hoverable ? "table hoverable" : "table";

    return html`
      <table class=${tableClass}>
        ${this.showHeader && this.columns.length > 0 ? this.renderHeader() : nothing}
        <tbody>
          ${this.loading ? this.renderLoadingSkeleton() : hasData ? this.renderRows() : this.renderEmptyState()}
        </tbody>
      </table>
    `;
  }

  private renderMobileView() {
    const sortableColumns = this.columns.filter((c) => c.sortable);

    return html`
      <div class="mobile-view">
        ${sortableColumns.length > 0 ? this.renderMobileSortButton() : nothing}
        ${this.loading
          ? this.renderMobileLoadingSkeleton()
          : this.data.length > 0
            ? this.renderTiles()
            : this.renderMobileEmptyState()}
      </div>
    `;
  }

  private renderMobileSortButton() {
    return html`
      <div class="sort-menu-container">
        <button class="sort-menu-trigger" @click=${this.toggleSortMenu}>
          Sort
          <ph-caret-up-down size="16"></ph-caret-up-down>
        </button>
        ${this._sortMenuOpen ? this.renderSortMenuContent() : nothing}
      </div>
    `;
  }

  private renderSortMenuContent() {
    const sortableColumns = this.columns.filter((c) => c.sortable);
    return html`
      <div class="sort-menu-popover">
        ${sortableColumns.map(
          (col) => html`
            <button class="sort-menu-item" @click=${() => this.handleSortClick(col.key)}>
              <span>${col.header}</span>
              ${this.renderSortIcon(col.key)}
            </button>
          `,
        )}
      </div>
    `;
  }

  private toggleSortMenu = () => {
    if (this._sortMenuOpen) {
      this._sortMenuOpen = false;
      this.cleanupSortMenuClickOutside();
    } else {
      this._sortMenuOpen = true;
      this.setupSortMenuClickOutside();
    }
  };

  private setupSortMenuClickOutside() {
    this._sortMenuClickOutsideHandler = (e: MouseEvent) => {
      const trigger = this.shadowRoot?.querySelector(".sort-menu-trigger");
      const popover = this.shadowRoot?.querySelector(".sort-menu-popover");
      if (!trigger) return;

      const inRect = (el: Element) => {
        const r = el.getBoundingClientRect();
        return e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      };

      if (inRect(trigger) || (popover && inRect(popover))) return;
      this._sortMenuOpen = false;
      this.cleanupSortMenuClickOutside();
    };
    setTimeout(() => {
      window.addEventListener("mousedown", this._sortMenuClickOutsideHandler!);
    }, 10);
  }

  private cleanupSortMenuClickOutside() {
    if (this._sortMenuClickOutsideHandler) {
      window.removeEventListener("mousedown", this._sortMenuClickOutsideHandler);
      this._sortMenuClickOutsideHandler = undefined;
    }
  }

  private renderTiles() {
    return this.data.map(
      (row, index) => html`
        <div
          class=${"tile" + (this.hoverable ? " tile-hoverable" : "")}
          @click=${() => this.handleRowClick(row, index)}
        >
          ${this.tileRender!(row, index)}
        </div>
      `,
    );
  }

  private renderMobileEmptyState() {
    return html`
      <div class="mobile-empty-state">
        <slot name="empty">
          <div class="empty-message">${this.emptyMessage}</div>
        </slot>
      </div>
    `;
  }

  private renderMobileLoadingSkeleton() {
    return Array.from({ length: this.loadingRows }, (_, i) => i).map(
      () => html`
        <div class="tile-skeleton">
          <skeleton-rect height="80px"></skeleton-rect>
        </div>
      `,
    );
  }

  private renderHeader() {
    return html`
      <thead>
        <tr>
          ${this.columns.map((column) => {
            const alignStyle = column.align ? `text-align: ${column.align}` : "";
            const classes = `${this.colClass(column.key)}${column.sortable ? " sortable" : ""}`;
            return html`
              <th
                style=${alignStyle}
                class=${classes}
                @click=${column.sortable ? () => this.handleSortClick(column.key) : nothing}
              >
                <span class="header-content">
                  ${column.header} ${column.sortable ? this.renderSortIcon(column.key) : nothing}
                </span>
              </th>
            `;
          })}
        </tr>
      </thead>
    `;
  }

  private handleSortClick(columnKey: string) {
    const multiDirection = this.sortStates[columnKey];
    const isCurrentColumn = this.sortState.column === columnKey;
    const currentDirection = multiDirection ?? (isCurrentColumn ? this.sortState.direction : "none");

    // Cycle: none -> asc -> desc -> none
    let newDirection: "asc" | "desc" | "none";
    if (currentDirection === "none") {
      newDirection = "asc";
    } else if (currentDirection === "asc") {
      newDirection = "desc";
    } else {
      newDirection = "none";
    }

    const previousState = { ...this.sortState };
    this.sortState = { column: columnKey, direction: newDirection };

    this.dispatchEvent(
      new CustomEvent("sort-change", {
        detail: {
          column: columnKey,
          direction: newDirection,
          previousState,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private renderSortIcon(columnKey: string) {
    const multiDirection = this.sortStates[columnKey];
    const isCurrentColumn = this.sortState.column === columnKey;
    const direction = multiDirection ?? (isCurrentColumn ? this.sortState.direction : "none");

    if (direction === "asc") {
      return html`<ph-caret-up class="sort-icon"></ph-caret-up>`;
    } else if (direction === "desc") {
      return html`<ph-caret-down class="sort-icon"></ph-caret-down>`;
    }
    return html`<ph-caret-up-down class="sort-icon"></ph-caret-up-down>`;
  }

  private renderRows() {
    return this.data.map(
      (row, rowIndex) => html`
        <tr @click=${() => this.handleRowClick(row, rowIndex)}>
          ${this.columns.map((column) => {
            const value = (row as Record<string, unknown>)[column.key];
            const alignStyle = column.align ? `text-align: ${column.align}` : "";
            const cellContent = column.render ? column.render(value, row, rowIndex) : (value ?? "");
            return html`<td class=${this.colClass(column.key)} style=${alignStyle}>${cellContent}</td>`;
          })}
        </tr>
      `,
    );
  }

  private handleRowClick(row: T, index: number) {
    if (this.disableRowClick) return;
    this.dispatchEvent(
      new CustomEvent("row-click", {
        detail: { row, index },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private renderEmptyState() {
    return html`
      <tr>
        <td colspan=${this.columns.length || 1}>
          <div class="empty-state">
            <slot name="empty">
              <div class="empty-message">${this.emptyMessage}</div>
            </slot>
          </div>
        </td>
      </tr>
    `;
  }

  private renderLoadingSkeleton() {
    const skeletonRows = Array.from({ length: this.loadingRows }, (_, index) => index);
    return skeletonRows.map(
      () => html`
        <tr class="skeleton-row">
          ${this.columns.map(
            (column) => html`
              <td class=${this.colClass(column.key)}>
                <skeleton-rect height="20px"></skeleton-rect>
              </td>
            `,
          )}
        </tr>
      `,
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "wow-base-table": WowBaseTable<unknown>;
  }
}
